// src/hooks/usePurchaseInteraction.js
//
// Handles click-to-toggle and shift-drag-to-box-select for the
// Imagery Purchase feature. Gated on purchaseActive so handlers
// are a no-op outside purchase mode.
//
// IMPORTANT: blocks with status="owned" are NOT selectable. The user
// can only select blocks with status="available".

import { useEffect, useRef } from "react";
import { usePurchaseStore } from "../stores/usePurchaseStore";
import { PURCHASE_LAYER_IDS } from "./usePurchaseLayers";

export function usePurchaseInteraction({ map, mapLoaded, purchaseActive }) {
  const boxRef = useRef({ start: null, el: null, dragging: false });

  useEffect(() => {
    if (!map || !mapLoaded || !purchaseActive) return;

    const canvas = map.getCanvasContainer();

    /* ---------- click-to-toggle ---------- */
    function handleClick(e) {

      const { activeGrid, tool, toggleBlock } = usePurchaseStore.getState();
      if (tool !== "click") return;


      const layerId = PURCHASE_LAYER_IDS[activeGrid].fill;
      console.log("[purchase click]", {
        tool,
        activeGrid,
        layerId,
        layerExists: !!map.getLayer(layerId),
        point: e.point,
      });

      if (!map.getLayer(layerId)) return;

      const feats = map.queryRenderedFeatures(e.point, { layers: [layerId] });
      console.log("[purchase click] features found:", feats.length);
      
      if (!feats.length) return;

      const f = feats[0];
      console.log("[purchase click] first feature:", {
        id: feats[0].id,
        status: feats[0].properties?.status,
        uid: feats[0].properties?.uid,
        area: feats[0].properties?.area_sq_mi,
      });

      // Skip owned blocks — not selectable
      if (f.properties?.status === "owned") return;

      const id = f.id;
      if (id == null) return;
      const uid = f.properties?.uid ?? f.properties?.ols_id ?? String(id);

      toggleBlock(activeGrid, id, uid, Number(f.properties?.area_sq_mi) || 0);

      e.preventDefault?.();
      e.originalEvent?.stopPropagation();
    }

    map.on("click", handleClick);

    /* ---------- shift-drag box select ---------- */
    function onMouseDown(e) {
      const { tool } = usePurchaseStore.getState();
      const boxActive = tool === "box" || e.shiftKey;
      if (!boxActive) return;
      if (e.button !== 0) return;

      e.preventDefault();
      map.dragPan.disable();
      boxRef.current.dragging = true;
      boxRef.current.start = [e.clientX, e.clientY];

      const el = document.createElement("div");
      el.className = "purchase-box-select";
      Object.assign(el.style, {
        position: "absolute",
        // Darker than the 1px / 0.1 it started at: the band is drawn OVER
        // satellite imagery, where a 10% blue wash is nearly invisible on
        // bright ground and the user cannot see what they are about to select.
        border: "1.5px dashed #1f9dff",
        background: "rgba(31, 157, 255, 0.28)",
        pointerEvents: "none",
        zIndex: 20,
      });
      canvas.appendChild(el);
      boxRef.current.el = el;

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    }

    function onMouseMove(e) {
      const { start, el } = boxRef.current;
      if (!start || !el) return;
      const [sx, sy] = start;
      const rect = canvas.getBoundingClientRect();
      const minX = Math.min(sx, e.clientX) - rect.left;
      const minY = Math.min(sy, e.clientY) - rect.top;
      const w = Math.abs(e.clientX - sx);
      const h = Math.abs(e.clientY - sy);
      el.style.left = `${minX}px`;
      el.style.top = `${minY}px`;
      el.style.width = `${w}px`;
      el.style.height = `${h}px`;
    }

    function onMouseUp(e) {
      const { start, el } = boxRef.current;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      map.dragPan.enable();
      boxRef.current.dragging = false;
      boxRef.current.start = null;
      if (el) el.remove();
      boxRef.current.el = null;

      if (!start) return;
      const rect = canvas.getBoundingClientRect();
      const p1 = { x: start[0] - rect.left, y: start[1] - rect.top };
      const p2 = { x: e.clientX - rect.left, y: e.clientY - rect.top };

      // Ignore tiny drags (user probably meant to click)
      if (Math.abs(p2.x - p1.x) < 3 && Math.abs(p2.y - p1.y) < 3) return;

      const { activeGrid, addBlocks } = usePurchaseStore.getState();
      const layerId = PURCHASE_LAYER_IDS[activeGrid].fill;
      if (!map.getLayer(layerId)) return;

      const feats = map.queryRenderedFeatures(
        [[p1.x, p1.y], [p2.x, p2.y]],
        { layers: [layerId] }
      );

      // Dedupe and skip owned blocks.
      const seen = new Set();
      const toAdd = [];
      for (const f of feats) {
        // Skip owned — not purchasable
        if (f.properties?.status === "owned") continue;

        const id = f.id;
        if (id == null || seen.has(id)) continue;
        seen.add(id);
        const uid = f.properties?.uid ?? f.properties?.ols_id ?? String(id);
        toAdd.push({
          id,
          uid,
          areaSqMi: Number(f.properties?.area_sq_mi) || 0,
        });
      }

      // Cap to prevent catastrophic selections at wide zoom.
      const MAX = 5000;
      if (toAdd.length > MAX) toAdd.length = MAX;

      addBlocks(activeGrid, toAdd);
    }

    canvas.addEventListener("mousedown", onMouseDown);

    /* ---------- cursor hints ---------- */
    // Change cursor based on what's under it
    function handleMouseMove(e) {
      const { activeGrid, tool } = usePurchaseStore.getState();
      if (tool !== "click") return;

      const layerId = PURCHASE_LAYER_IDS[activeGrid].fill;
      if (!map.getLayer(layerId)) return;

      const feats = map.queryRenderedFeatures(e.point, { layers: [layerId] });
      if (!feats.length) {
        map.getCanvas().style.cursor = "crosshair";
        return;
      }

      const status = feats[0].properties?.status;
      if (status === "owned") {
        map.getCanvas().style.cursor = "not-allowed";
      } else {
        map.getCanvas().style.cursor = "pointer";
      }
    }

    map.on("mousemove", handleMouseMove);

    const prevCursor = map.getCanvas().style.cursor;
    map.getCanvas().style.cursor = "crosshair";

    return () => {
      map.off("click", handleClick);
      map.off("mousemove", handleMouseMove);
      canvas.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      map.dragPan.enable();
      map.getCanvas().style.cursor = prevCursor;
      if (boxRef.current.el) boxRef.current.el.remove();
      boxRef.current = { start: null, el: null, dragging: false };
    };
  }, [map, mapLoaded, purchaseActive]);
}